import { ActionType as AccessTokenActionType, Action as AccessTokenAction } from '~/AccessToken';
import { ReferencedProductType } from '~/Api/RoPriceReferencing/MaxPrice';
import { RoMaxPrice, Update as ApiUpdate, ValidationResult } from '~/Api/User/ProductGroup/RoMaxPrice';
import { Settings } from '~/Api/User/RoMaxPriceSettings';

import { Action, ActionType } from './Action';

export interface RoMaxPriceState {
	roMaxPrice: RoMaxPrice | null;
	isFetching: boolean;
	request: Promise<any>;
}

type UpdateState =
	| {
			name: 'scheduled';
			timeoutId: NodeJS.Timeout;
			data: ApiUpdate;
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	isFetching: boolean;
	request: Promise<any>;
	validationResult: ValidationResult | null;
}

export interface UserSettings {
	isFetching: boolean;
	request: Promise<any>;
	settings: Settings | null;
}

export interface State {
	byId: { [key in string]: RoMaxPriceState };
	export: Export | null;
	update: Update | null;
	userSettings: UserSettings | null;
	userSettingsUpdateRequest: Promise<any> | null;
	productTypes: {
		isFetching: boolean;
		request: Promise<any>;
		types: ReferencedProductType[] | null;
	} | null;
}

export const createState = (): State => ({
	byId: {},
	export: null,
	update: null,
	userSettings: null,
	userSettingsUpdateRequest: null,
	productTypes: null,
});

// tslint:disable-next-line: cyclomatic-complexity
export const reducer = (state: State = createState(), action: Action | AccessTokenAction): State => {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						roMaxPrice: state.byId[action.groupId]?.roMaxPrice ?? null,
						isFetching: true,
						request: action.request,
					},
				},
			};

		case ActionType.receive: {
			const roMaxPrice = state.byId[action.groupId];
			if (roMaxPrice?.request !== action.request) {
				return state;
			}

			if (action.roMaxPrice === null) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...roMaxPrice,
						roMaxPrice: action.roMaxPrice,
						isFetching: false,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						data: action.update,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			if (action.roMaxPrice !== null && action.groupId in byId) {
				byId[action.groupId] = {
					...byId[action.groupId],
					roMaxPrice: action.roMaxPrice,
				};
			}

			return {
				...state,
				byId,
				export: null,
				update: {
					groupId: action.groupId,
					state: action.roMaxPrice !== null ? { name: 'updated' } : { name: 'failed' },
				},
			};
		}

		case ActionType.requestExport:
			return {
				...state,
				export: {
					groupId: action.groupId,
					isFetching: true,
					request: action.request,
					validationResult: null,
				},
			};

		case ActionType.receiveExport:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case ActionType.requestUserSettings:
			return {
				...state,
				userSettings: {
					isFetching: true,
					request: action.request,
					settings: null,
				},
			};

		case ActionType.receiveUserSettings:
			if (state.userSettings?.request !== action.request) {
				return state;
			}

			return {
				...state,
				userSettings: {
					...state.userSettings,
					isFetching: false,
					settings: action.settings,
				},
			};

		case ActionType.requestUserSettingsUpdate:
			return {
				...state,
				userSettingsUpdateRequest: action.request,
			};

		case ActionType.receiveUserSettingsUpdate:
			if (state.userSettingsUpdateRequest !== action.request) {
				return state;
			}

			return {
				...state,
				userSettings: {
					isFetching: false,
					request: action.request,
					settings: action.settings,
				},
				userSettingsUpdateRequest: null,
			};

		case ActionType.requestProductTypes:
			return {
				...state,
				productTypes: {
					isFetching: true,
					request: action.request,
					types: null,
				},
			};

		case ActionType.receiveProductTypes:
			if (state.productTypes?.request !== action.request) {
				return state;
			}

			return {
				...state,
				productTypes:
					action.types !== null
						? {
								...state.productTypes,
								isFetching: true,
								request: action.request,
								types: action.types,
						  }
						: null,
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
};

export const selectRoMaxPrice = (state: State, groupId: string): RoMaxPriceState | null => state.byId[groupId];

export const selectExport = (state: State, groupId: string): Export | null =>
	state.export?.groupId === groupId ? state.export : null;
